import React, { useState, useEffect } from "react";
import api from "../../api/api";

const fmt = (n, decimals = 2) =>
  Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: decimals });

const formatDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return d; }
};

function StatusBadge({ status }) {
  const map = {
    ACTIVE:    { bg: "#f0fdf4", color: "#166534" },
    PAUSED:    { bg: "#fef9c3", color: "#854d0e" },
    CANCELLED: { bg: "#fef2f2", color: "#991b1b" },
    EXPIRED:   { bg: "#f3f4f6", color: "#374151" },
  };
  const s = map[(status || "").toUpperCase()] || map.EXPIRED;
  return (
    <span style={{ display: "inline-block", padding: "3px 8px", borderRadius: 6, fontSize: 10, fontWeight: 700, background: s.bg, color: s.color }}>
      {status || "UNKNOWN"}
    </span>
  );
}

const InvestorSubscriptions = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [subs, setSubs]       = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.getInvestorSubscriptions();
        const raw = res.data;
        setSubs(Array.isArray(raw) ? raw : []);
        setError(null);
      } catch (err) {
        console.error("Subscriptions fetch error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="page">
        <div style={{ textAlign: "center", padding: "60px 20px", color: "rgba(0,0,0,0.4)", fontSize: 13 }}>Loading subscriptions...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="page">
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 12, padding: "16px 20px", color: "#991b1b", fontSize: 13 }}>
          Failed to load subscriptions: {error}
        </div>
      </div>
    );
  }

  const active   = subs.filter((s) => (s.status || "").toUpperCase() === "ACTIVE");
  const inactive = subs.filter((s) => (s.status || "").toUpperCase() !== "ACTIVE");

  const cardStyle = { background: "rgba(255,255,255,0.96)", borderRadius: 14, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", border: "1px solid rgba(0,0,0,0.07)", overflow: "hidden", marginBottom: 16 };
  const headerStyle = { padding: "16px 20px", borderBottom: "1px solid rgba(0,0,0,0.06)" };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Analyst Subscriptions</h1>
          <p className="page-subtitle">Manage your analyst signal set subscriptions — billing, execution mode, and subscription history.</p>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 12, marginBottom: 20 }}>
        {[
          { label: "TOTAL SUBSCRIPTIONS", value: subs.length, accent: null },
          { label: "ACTIVE", value: active.length, accent: "var(--success)" },
          { label: "INACTIVE / ENDED", value: inactive.length, accent: null },
        ].map((c) => (
          <div key={c.label} style={{ background: "rgba(255,255,255,0.96)", borderRadius: 14, boxShadow: "0 4px 16px rgba(0,0,0,0.08)", border: c.accent ? `1.5px solid ${c.accent}` : "1px solid rgba(0,0,0,0.07)", padding: "16px 20px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "rgba(0,0,0,0.45)", marginBottom: 8 }}>{c.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: c.accent || "rgba(0,0,0,0.87)" }}>{c.value}</div>
          </div>
        ))}
      </div>

      <div style={cardStyle}>
        <div style={headerStyle}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,0.85)" }}>Active Subscriptions</div>
          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>Currently active analyst signal sets with billing details</div>
        </div>
        {active.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(0,0,0,0.35)", fontSize: 12 }}>
            <div style={{ fontSize: 28, marginBottom: 10 }}></div>
            No active subscriptions. Browse the Marketplace to find analyst signal sets.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ margin: 0 }}>
              <thead>
                <tr><th>Analyst</th><th>Signal Set</th><th>Plan</th><th>Mode</th><th>Subscribed</th><th>Next Billing</th><th>Status</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {active.map((s, i) => (
                  <tr key={i}>
                    <td className="text-xs" style={{ fontWeight: 700 }}>{s.analyst_name || s.analyst_id || "—"}</td>
                    <td className="text-xs">{s.signal_set_name || s.signal_set_id || "—"}</td>
                    <td className="text-xs">{s.plan_price ? `$${fmt(s.plan_price, 0)}/mo` : "—"}</td>
                    <td className="text-xs">{s.mode || "—"}</td>
                    <td className="text-xs">{formatDate(s.subscribed_at || s.created_at)}</td>
                    <td className="text-xs">{formatDate(s.next_billing)}</td>
                    <td className="text-xs"><StatusBadge status={s.status} /></td>
                    <td className="text-xs">
                      <button className="btn btn-xs btn-ghost" onClick={() => alert("Cancel subscription — backend endpoint coming soon.")}>Cancel</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={cardStyle}>
        <div style={headerStyle}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "rgba(0,0,0,0.85)" }}>Previous Subscriptions</div>
          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginTop: 2 }}>Cancelled, expired, or paused subscriptions</div>
        </div>
        {inactive.length === 0 ? (
          <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(0,0,0,0.35)", fontSize: 12 }}>No previous subscriptions.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ margin: 0 }}>
              <thead>
                <tr><th>Analyst</th><th>Signal Set</th><th>Plan</th><th>Subscribed</th><th>Ended</th><th>Status</th></tr>
              </thead>
              <tbody>
                {inactive.map((s, i) => (
                  <tr key={i}>
                    <td className="text-xs" style={{ fontWeight: 700 }}>{s.analyst_name || s.analyst_id || "—"}</td>
                    <td className="text-xs">{s.signal_set_name || s.signal_set_id || "—"}</td>
                    <td className="text-xs">{s.plan_price ? `$${fmt(s.plan_price, 0)}/mo` : "—"}</td>
                    <td className="text-xs">{formatDate(s.subscribed_at || s.created_at)}</td>
                    <td className="text-xs">{formatDate(s.ended_at || s.updated_at)}</td>
                    <td className="text-xs"><StatusBadge status={s.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 10, padding: "12px 16px", fontSize: 12, color: "#1e40af" }}>
        <strong>Note:</strong> Performance fees (10% HWM) apply only on auto-mode subscriptions where profits exceed the high-water mark. Subscription fees are billed monthly per analyst.
      </div>
    </div>
  );
};

export default InvestorSubscriptions;
